import { BOOSTS, type BoostType } from '@cube-rush/shared';
import { ru } from '../../i18n/ru';
import { CONTROL_ATTR } from '../../input/GameInput';
import type { PadStyle } from '../../input/pads';
import { rgba } from '../../render/color';
import type { HudActions } from './actions';
import type { HudDom } from './hudDom';
import type { HudModel } from './hudModel';

export interface BoostBarProps {
  hud: HudModel;
  dom: HudDom;
  actions: HudActions;
}

const control = { [CONTROL_ATTR]: '' };

/**
 * The two boost slots (bottom right of the player's view) and, left of them, the boosts that
 * are running, each with a ring that empties as its time runs out (HudDom.boostTimers).
 * A slot fires on pointerdown — at once, and also while another finger holds the jump.
 * The whole bar is one low row along the bottom edge (HudDom.bottomControls): in cube
 * stretches the camera keeps the floor above it, so spikes coming up on the floor are not
 * hidden behind it; tall vehicle corridors may still run behind the see-through buttons.
 * Each slot names its key (1, 2) on computers, and once a gamepad was used its button (X, Y)
 * on every screen.
 */
export function BoostBar({ hud, dom, actions }: BoostBarProps) {
  const inventory = hud.inventory.value;
  const active = hud.activeBoosts.value;
  const ready = hud.boostsReady.value;
  const pad = hud.padStyle.value;
  const rect = hud.mainRect.value;
  const style =
    rect.w > 0
      ? { top: `${rect.y}px`, left: `${rect.x}px`, width: `${rect.w}px`, height: `${rect.h}px` }
      : undefined;
  return (
    <div class="hud-boosts" style={style}>
      <div class="hud-boosts__corner" ref={(element) => dom.bottomControl('boosts', element)}>
        {active.length > 0 && (
          <div class="hud-boosts__active">
            {active.map((type) => (
              <ActiveBoost key={type} type={type} dom={dom} />
            ))}
          </div>
        )}
        <div class="hud-boosts__slots" {...control}>
          {inventory.map((type, slot) => (
            <BoostSlot
              key={slot}
              slot={slot}
              type={type}
              ready={ready}
              pad={pad}
              onUse={() => actions.useBoost(slot)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function BoostSlot({
  slot,
  type,
  ready,
  pad,
  onUse,
}: {
  slot: number;
  type: BoostType | null;
  ready: boolean;
  /** A gamepad was used: its button for the slot instead of the key. */
  pad: PadStyle | null;
  onUse(): void;
}) {
  const boost = type ? BOOSTS[type] : null;
  const usable = boost !== null && ready;
  return (
    <button
      type="button"
      class={`hud-boost${boost ? '' : ' hud-boost--empty'}${usable ? '' : ' hud-boost--waiting'}`}
      style={boost ? { '--boost': boost.color, '--glow': rgba(boost.color, 0.55) } : undefined}
      aria-label={ru.game.boostSlot(slot + 1, boost?.name ?? null)}
      aria-disabled={!usable}
      onPointerDown={(event) => {
        // No focus, no click delay; the press is not a jump either (HUD control).
        event.preventDefault();
        if (usable) onUse();
      }}
      onClick={(event) => {
        // Keyboard activation (Enter on a focused button) has no pointerdown.
        if (event.detail === 0 && usable) onUse();
      }}
    >
      <span class="hud-boost__emoji" aria-hidden="true">
        {boost ? boost.emoji : ''}
      </span>
      <span class={`hud-boost__key${pad ? ' hud-boost__key--pad' : ''}`} aria-hidden="true">
        {pad ? (ru.gamepad.boostKeys[pad][slot] ?? slot + 1) : slot + 1}
      </span>
    </button>
  );
}

function ActiveBoost({ type, dom }: { type: BoostType; dom: HudDom }) {
  const boost = BOOSTS[type];
  return (
    <span
      class="hud-boost-active"
      ref={(element) => dom.boostTimer(type, element)}
      style={{ '--boost': boost.color, '--glow': rgba(boost.color, 0.6), '--left': '100%' }}
      role="img"
      aria-label={ru.game.boostActive(boost.name)}
    >
      <span aria-hidden="true">{boost.emoji}</span>
    </span>
  );
}
